import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { publicPaymentApi } from "../api/client";

interface PaymentStatusData {
  status: string;
  orderId: string;
  orderNumber?: number;
  estimatedMinutes?: number;
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [orderData, setOrderData] = useState<PaymentStatusData | null>(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setPolling(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      try {
        const data = await publicPaymentApi.checkPaymentStatus(sessionId!);
        setOrderData(data);

        // Keep polling until we have a confirmed order or exceed attempts
        if (data.status === "paid" || data.orderId) {
          setPolling(false);
          return;
        }
      } catch (err) {
        // Silently retry on error until max attempts
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = setTimeout(poll, 2000);
      } else {
        setPolling(false);
        setError("Impossibile verificare lo stato del pagamento.");
      }
    }

    poll();

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <CheckCircle2 size={80} className="text-green-500" strokeWidth={1.5} />
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Pagamento completato!
        </h1>

        {/* Subtitle */}
        <p className="text-gray-500 text-lg mb-8">
          Il tuo ordine e stato confermato.
        </p>

        {/* Order details card */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-left space-y-3">
          {polling && !orderData && (
            <div className="flex items-center gap-3 text-gray-500">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin flex-shrink-0" />
              <span>Recupero dettagli ordine...</span>
            </div>
          )}

          {orderData?.orderNumber && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500 font-medium">Numero ordine</span>
              <span className="text-gray-800 font-bold text-xl">
                #{orderData.orderNumber}
              </span>
            </div>
          )}

          {orderData?.estimatedMinutes && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500 font-medium">Tempo stimato</span>
              <span className="text-blue-600 font-semibold">
                ~{orderData.estimatedMinutes} minuti
              </span>
            </div>
          )}

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
        </div>

        {/* Back to menu link */}
        <Link
          to="/"
          className="inline-block w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg min-h-touch flex items-center justify-center"
        >
          Torna al menu
        </Link>
      </div>
    </div>
  );
}
